<script module>
    export { default as layout } from '../layouts/AppLayout.svelte';
</script>

<script lang="ts">
    import ConfirmModal from '../components/ConfirmModal.svelte';
    import Modal from '../components/Modal.svelte';
    import SocialIcon from '../components/SocialIcon.svelte';
    import ToggleSwitch from '../components/ToggleSwitch.svelte';
    import { useForm, page, router } from '@inertiajs/svelte';
    import { fade } from 'svelte/transition';

    const user = $derived($page?.props?.auth?.user);
    const errors = $derived($page?.props?.errors ?? {});
    const socialAccounts: Record<string, { provider_name: string | null; provider_email: string | null }> = $derived($page?.props?.socialAccounts ?? {});

    const providers = [
        { key: 'google', label: 'Google' },
        { key: 'apple', label: 'Apple' },
        { key: 'orcid', label: 'ORCiD' },
    ];

    const avatarColors = [
        'bg-blue-600', 'bg-emerald-600', 'bg-violet-600', 'bg-amber-600',
        'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
    ];
    const avatarColor = $derived(user ? avatarColors[user.id % avatarColors.length] : avatarColors[0]);
    const userInitial = $derived(user?.name ? user.name.charAt(0).toUpperCase() : '?');

    const profileForm = useForm({ name: '', email: '' });
    const hasPassword = $derived(user?.has_password ?? true);
    const passwordForm = useForm({ current_password: '', password: '', password_confirmation: '' });
    let emailReminders = $state(true);
    let leaderboardOptIn = $state(true);

    let profileSaved = $state(false);
    let passwordSaved = $state(false);
    let prefSaved = $state(false);
    let prefSaving = $state(false);

    $effect(() => {
        if (user && !$profileForm.name) {
            $profileForm.name = user.name;
            $profileForm.email = user.email;
        }
        if (user) {
            emailReminders = user.email_reminders;
            leaderboardOptIn = user.leaderboard_opt_in;
        }
    });

    function submitProfile(e: SubmitEvent) {
        e.preventDefault();
        $profileForm.patch('/profile', {
            onSuccess: () => {
                profileSaved = true;
                setTimeout(() => { profileSaved = false; }, 2000);
            },
        });
    }

    function submitPassword(e: SubmitEvent) {
        e.preventDefault();
        $passwordForm.patch('/profile/password', {
            onSuccess: () => {
                $passwordForm.current_password = '';
                $passwordForm.password = '';
                $passwordForm.password_confirmation = '';
                passwordModalOpen = false;
                passwordSaved = true;
                setTimeout(() => { passwordSaved = false; }, 2000);
            },
        });
    }

    function savePreferences() {
        prefSaving = true;
        router.patch('/profile/preferences', {
            email_reminders: emailReminders,
            leaderboard_opt_in: leaderboardOptIn,
        }, {
            preserveScroll: true,
            onSuccess: () => {
                prefSaved = true;
                setTimeout(() => { prefSaved = false; }, 2000);
            },
            onFinish: () => { prefSaving = false; },
        });
    }

    let passwordModalOpen = $state(false);
    let disconnectModalOpen = $state(false);
    let pendingDisconnect: string | null = $state(null);
    const pendingDisconnectLabel = $derived(
        providers.find(p => p.key === pendingDisconnect)?.label ?? ''
    );

    function confirmDisconnect(provider: string) {
        pendingDisconnect = provider;
        disconnectModalOpen = true;
    }

    function executeDisconnect() {
        if (pendingDisconnect) {
            router.delete(`/profile/social/${pendingDisconnect}`);
            pendingDisconnect = null;
        }
    }
</script>

<svelte:head>
    <title>Profile — Case of the Day</title>
</svelte:head>

<div class="max-w-2xl mx-auto space-y-8">
        <!-- Identity Header -->
        <div class="flex items-center gap-4">
            <div class="flex items-center justify-center size-14 rounded-full text-white text-xl font-bold {avatarColor}">
                {userInitial}
            </div>
            <div>
                <h1 class="text-2xl font-bold text-gray-100">{user?.name ?? 'Profile'}</h1>
                <p class="text-sm text-gray-500">{user?.email ?? ''}</p>
            </div>
        </div>

        <!-- Profile Information -->
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <div class="flex items-start gap-3 mb-4">
                <div class="flex items-center justify-center size-9 rounded-lg bg-cyan-950 text-cyan-400 shrink-0">
                    <svg class="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M144 128a80 80 0 1 1 160 0 80 80 0 1 1 -160 0zm208 0a128 128 0 1 0 -256 0 128 128 0 1 0 256 0zM48 480c0-70.7 57.3-128 128-128l96 0c70.7 0 128 57.3 128 128l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8c0-97.2-78.8-176-176-176l-96 0C78.8 304 0 382.8 0 480l0 8c0 13.3 10.7 24 24 24s24-10.7 24-24l0-8z"/></svg>
                </div>
                <div>
                    <h2 class="text-lg font-semibold text-gray-200">Profile Information</h2>
                    <p class="text-sm text-gray-500">Update your name and email address</p>
                </div>
            </div>
            <form onsubmit={submitProfile} class="space-y-4">
                <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                    <input
                        bind:value={$profileForm.name}
                        type="text"
                        required
                        class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.name ? 'border-red-500' : 'border-gray-700'}"
                    />
                    {#if errors.name}<p class="text-red-500 text-sm mt-1">{errors.name}</p>{/if}
                </div>
                <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1">Email</label>
                    <input
                        bind:value={$profileForm.email}
                        type="email"
                        autocomplete="email"
                        required
                        class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.email ? 'border-red-500' : 'border-gray-700'}"
                    />
                    {#if errors.email}<p class="text-red-500 text-sm mt-1">{errors.email}</p>{/if}
                </div>
                <div class="flex items-center gap-3">
                    <button
                        type="submit"
                        disabled={$profileForm.processing}
                        class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm hover:bg-cyan-500 disabled:opacity-50"
                    >
                        Save Changes
                    </button>
                    {#if profileSaved}
                        <span transition:fade class="text-sm text-green-400 font-medium">Saved</span>
                    {/if}
                </div>
            </form>
        </div>

        <!-- Change Password -->
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <div class="flex items-center gap-3">
                <div class="flex items-center justify-center size-9 rounded-lg bg-cyan-950 text-cyan-400 shrink-0">
                    <svg class="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M208 176c0-70.7 57.3-128 128-128s128 57.3 128 128-57.3 128-128 128c-10.4 0-20.5-1.2-30.1-3.6-8.1-2-16.7 .5-22.6 6.4L254.1 336 200 336c-13.3 0-24 10.7-24 24l0 40-40 0c-13.3 0-24 10.7-24 24l0 40-64 0 0-78.1 157.2-157.2c5.9-5.9 8.3-14.5 6.4-22.6-2.3-9.6-3.6-19.7-3.6-30.1zM336 0c-97.2 0-176 78.8-176 176 0 9.5 .7 18.8 2.2 27.9L7 359c-4.5 4.5-7 10.6-7 17L0 488c0 13.3 10.7 24 24 24l112 0c13.3 0 24-10.7 24-24l0-40 40 0c13.3 0 24-10.7 24-24l0-40 40 0c6.4 0 12.5-2.5 17-7l27.2-27.2c9.1 1.4 18.4 2.2 27.9 2.2 97.2 0 176-78.8 176-176S433.2 0 336 0zm32 176a32 32 0 1 0 0-64 32 32 0 1 0 0 64z"/></svg>
                </div>
                <div class="flex-1">
                    <h2 class="text-lg font-semibold text-gray-200">Password</h2>
                    <p class="text-sm text-gray-500">{hasPassword ? 'Ensure your account stays secure' : 'Set a password to enable email/password login'}</p>
                </div>
                <div class="flex items-center gap-3">
                    {#if passwordSaved}
                        <span transition:fade class="text-sm text-green-400 font-medium">Saved</span>
                    {/if}
                    <button
                        onclick={() => { passwordModalOpen = true; }}
                        class="text-sm font-medium text-cyan-400 hover:text-cyan-300 px-3 py-1.5 rounded-md hover:bg-cyan-950 transition-colors"
                    >
                        {hasPassword ? 'Change Password' : 'Set Password'}
                    </button>
                </div>
            </div>
        </div>

        <!-- Connected Accounts -->
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <div class="flex items-start gap-3 mb-4">
                <div class="flex items-center justify-center size-9 rounded-lg bg-cyan-950 text-cyan-400 shrink-0">
                    <svg class="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512"><path fill="currentColor" d="M427.5 88c-21.4 0-42.2 6.9-59.3 19.4-11.4-11.7-24.4-22.1-38.4-30.7 27-23.6 61.7-36.7 97.7-36.7 82 0 148.5 66.5 148.5 148.5 0 39.4-15.6 77.1-43.5 105l-79.1 79.1c-27.8 27.8-65.6 43.5-105 43.5-82 0-148.5-66.5-148.5-148.5 0-17.9 3.2-35.5 9.4-51.9 4.6-12.4 18.5-18.7 30.9-14.1S259 220 254.4 232.4c-4.2 11.1-6.4 23-6.4 35.2 0 55.5 45 100.5 100.5 100.5 26.6 0 52.2-10.6 71-29.4l79.1-79.1C517.4 240.6 528 215.1 528 188.5 528 133 483 88 427.5 88zm-200 56c-26.6 0-52.2 10.6-71 29.4L77.4 252.5c-18.8 18.8-29.4 44.4-29.4 71 0 55.5 45 100.5 100.5 100.5 21.4 0 42.2-6.9 59.3-19.4 11.4 11.8 24.4 22.1 38.4 30.7-27 23.6-61.7 36.7-97.7 36.7-82 0-148.5-66.5-148.5-148.5 0-39.4 15.6-77.1 43.5-105l79.1-79.1c27.8-27.8 65.6-43.5 105-43.5 82 0 148.5 66.5 148.5 148.5 0 17.9-3.2 35.5-9.4 51.9-4.6 12.4-18.5 18.7-30.9 14.1S317 292 321.6 279.6c4.2-11.1 6.4-23 6.4-35.1 0-55.5-45-100.5-100.5-100.5z"/></svg>
                </div>
                <div>
                    <h2 class="text-lg font-semibold text-gray-200">Connected Accounts</h2>
                    <p class="text-sm text-gray-500">Link your social accounts for single sign-on</p>
                </div>
            </div>
            <div class="divide-y divide-gray-800">
                {#each providers as provider (provider.key)}
                    {@const account = socialAccounts[provider.key]}
                    <div class="flex items-center gap-4 py-4 first:pt-0 last:pb-0">
                        <div class="flex-shrink-0 flex items-center justify-center size-10 rounded-full {account ? 'bg-gray-800' : 'bg-gray-950'}">
                            <SocialIcon provider={provider.key} colored class="size-5" />
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-gray-100">{provider.label}</p>
                            {#if account}
                                <p class="text-xs text-gray-500 truncate">
                                    {#if account.provider_name}{account.provider_name}{/if}
                                    {#if account.provider_name && account.provider_email} &middot; {/if}
                                    {#if account.provider_email}{account.provider_email}{/if}
                                </p>
                            {:else}
                                <p class="text-xs text-gray-400">Not connected</p>
                            {/if}
                        </div>
                        {#if account}
                            <button
                                onclick={() => confirmDisconnect(provider.key)}
                                class="text-sm font-medium text-red-400 hover:text-red-300 px-3 py-1.5 rounded-md hover:bg-red-950 transition-colors"
                            >
                                Disconnect
                            </button>
                        {:else}
                            <a
                                href={`/auth/${provider.key}/redirect`}
                                class="text-sm font-medium text-cyan-400 hover:text-cyan-300 px-3 py-1.5 rounded-md hover:bg-cyan-950 transition-colors"
                            >
                                Connect
                            </a>
                        {/if}
                    </div>
                {/each}
            </div>
        </div>

        <!-- Preferences -->
        <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
            <div class="flex items-start gap-3 mb-4">
                <div class="flex items-center justify-center size-9 rounded-lg bg-cyan-950 text-cyan-400 shrink-0">
                    <svg class="size-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path fill="currentColor" d="M224 0c-13.3 0-24 10.7-24 24l0 9.7C118.6 45.3 56 115.4 56 200l0 14.5c0 37.7-10 74.7-29 107.3L5.1 359.2C1.8 365 0 371.5 0 378.2 0 399.1 16.9 416 37.8 416l372.4 0c20.9 0 37.8-16.9 37.8-37.8 0-6.7-1.8-13.3-5.1-19L421 321.7c-19-32.6-29-69.6-29-107.3l0-14.5c0-84.6-62.6-154.7-144-166.3l0-9.7c0-13.3-10.7-24-24-24zM392.4 368l-336.9 0 12.9-22.1C91.7 306 104 260.6 104 214.5l0-14.5c0-66.3 53.7-120 120-120s120 53.7 120 120l0 14.5c0 46.2 12.3 91.5 35.5 131.4L392.4 368zM156.1 464c9.9 28 36.6 48 67.9 48s58-20 67.9-48l-135.8 0z"/></svg>
                </div>
                <div>
                    <h2 class="text-lg font-semibold text-gray-200">Preferences</h2>
                    <p class="text-sm text-gray-500">Control notifications and visibility</p>
                </div>
            </div>
            <div class="divide-y divide-gray-800">
                <div class="flex items-center justify-between gap-4 py-4 first:pt-0">
                    <div>
                        <p class="text-sm font-medium text-gray-300">Email reminders</p>
                        <p class="text-xs text-gray-500">Receive daily email reminders about new cases</p>
                    </div>
                    <ToggleSwitch bind:checked={emailReminders} disabled={prefSaving} onchange={savePreferences} />
                </div>
                <div class="flex items-center justify-between gap-4 py-4 last:pb-0">
                    <div>
                        <p class="text-sm font-medium text-gray-300">Show name on leaderboard</p>
                        <p class="text-xs text-gray-500">Display your name on the leaderboard instead of "Anonymous"</p>
                    </div>
                    <ToggleSwitch bind:checked={leaderboardOptIn} disabled={prefSaving} onchange={savePreferences} />
                </div>
            </div>
            {#if prefSaved}
                <p transition:fade class="text-sm text-green-400 font-medium mt-3">Saved</p>
            {/if}
        </div>
    </div>

    <Modal bind:open={passwordModalOpen} title={hasPassword ? 'Change Password' : 'Set Password'}>
        <form onsubmit={submitPassword} class="space-y-4">
            {#if hasPassword}
                <div>
                    <label class="block text-sm font-medium text-gray-300 mb-1">Current Password</label>
                    <input
                        bind:value={$passwordForm.current_password}
                        type="password"
                        autocomplete="current-password"
                        required
                        class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.current_password ? 'border-red-500' : 'border-gray-700'}"
                    />
                    {#if errors.current_password}<p class="text-red-500 text-sm mt-1">{errors.current_password}</p>{/if}
                </div>
            {/if}
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">New Password</label>
                <input
                    bind:value={$passwordForm.password}
                    type="password"
                    autocomplete="new-password"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.password ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.password}<p class="text-red-500 text-sm mt-1">{errors.password}</p>{/if}
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-300 mb-1">Confirm New Password</label>
                <input
                    bind:value={$passwordForm.password_confirmation}
                    type="password"
                    autocomplete="new-password"
                    required
                    class="w-full border rounded-md px-3 py-2 text-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-colors {errors.password_confirmation ? 'border-red-500' : 'border-gray-700'}"
                />
                {#if errors.password_confirmation}<p class="text-red-500 text-sm mt-1">{errors.password_confirmation}</p>{/if}
            </div>
            <div class="flex justify-end gap-3 pt-2">
                <button
                    type="button"
                    onclick={() => { passwordModalOpen = false; }}
                    class="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-gray-800"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={$passwordForm.processing}
                    class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm hover:bg-cyan-500 disabled:opacity-50"
                >
                    {hasPassword ? 'Update Password' : 'Set Password'}
                </button>
            </div>
        </form>
    </Modal>

    <ConfirmModal
        bind:open={disconnectModalOpen}
        title="Disconnect {pendingDisconnectLabel}?"
        message="You will no longer be able to sign in with this {pendingDisconnectLabel} account. You can reconnect it at any time."
        confirmLabel="Disconnect"
        onconfirm={executeDisconnect}
        oncancel={() => { pendingDisconnect = null; }}
    />
